import { ISparkSidebarCollapseChange, ISparkSidebarConfig, ISparkSidebarList, ISparkSidebarMenu, ISparkSidebarSubMenu } from '@domains/frontend/templates/spark';
import { Component, ElementRef, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { EnumLanguage } from '@libs/constants';
import { Util } from '@libs/utilities/util';
import { NgModalAssistantService } from '@libs/frameworks/angular';

import { RESEARCH_SIDEBAR_LIST } from './sidebar-menu/research-sidebar.data';
import { UPLOAD_SIDEBAR_LIST } from './sidebar-menu/upload-sidebar.data';
import { EnumSidebarPage } from './sidebar-pages.interface';
import { IPagesListModuleAccess } from './page-lists/pages-list.interface';
import { RESEARCH_PAGE_LIST_MODULE_DATA } from './page-lists/research-pages/research-module.data';
import { UPLOAD_PAGE_LIST_MODULE_DATA } from './page-lists/upload-pages/upload-module.data';
import { StatesService } from '../../../services/shared/states/states.service';
import { AppearancePreferenceService } from '../../../services/shared/layout-preferences/appearance-preference.service';
import { APPEARANCE_PREFERENCE_VISIBILITY_ANIMATION } from '../../../services/shared/layout-preferences/appearance-preference.animation';
import { EnumWebAppTool, IWebAppTool } from '../../../shared/constants/app.constant';
import { IUserInfo } from '../../../requests/shared/user/user.request';
import { MASCOT_IMG_LINKS } from '../../../../assets';
import { ENVIRONMENT } from '../../../../environments';


@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styleUrls: ['./sidebar.component.scss'],
	standalone: false,
	animations: [APPEARANCE_PREFERENCE_VISIBILITY_ANIMATION]
})
export class SidebarComponent implements OnInit, OnDestroy {

	//** Configuration */
	readonly MASCOT_IMG_LINKS 			  : typeof MASCOT_IMG_LINKS = MASCOT_IMG_LINKS;
	readonly EnumLanguage				  : typeof EnumLanguage 	= EnumLanguage;
	readonly EnumWebAppTool			  : typeof EnumWebAppTool = EnumWebAppTool;
	readonly FACEBOOK_CHAT_ALLOWED_ROUTES : string[] 	   			= ['/support'];
	readonly FAVORITE_SLOT_INDEXES		  : number[]				= [0, 1, 2, 3];
	readonly COMMUNITY_LINKS			  : ISidebarCommunityLink[] = [
		{
			label	 : 'Open Flying Discord',
			icon	 : 'discord',
			platform: 'discord',
			url		 : 'https://discord.gg/TbpwAXnkdr'
		},
		{
			label	 : 'Open English YouTube channel',
			icon	 : 'youtube',
			platform: 'youtube',
			badge	 : 'EN',
			url		 : 'https://www.youtube.com/@flyingupload7908'
		},
		{
			label	 : 'Open German YouTube channel',
			icon	 : 'youtube',
			platform: 'youtube',
			badge	 : 'DE',
			url		 : 'https://www.youtube.com/@flyingupload-deutsch3602'
		},
		{
			label	 : 'Open Flying Instagram',
			icon	 : 'instagram',
			platform: 'instagram',
			url		 : 'https://www.instagram.com/flyingresearch/'
		}
	];
	private readonly FAVORITES_STORAGE_KEY: string				= 'flying-sidebar-favorites-v1';
	private readonly EXPANSION_STORAGE_KEY: string				= 'flying-sidebar-expansion-v1';
	private readonly SIDEBAR_HIDDEN_PAGES : EnumSidebarPage[]		= ENVIRONMENT.FEATURE_FLAGS.SIDEBAR_HIDDEN_PAGES;
	private readonly PAGE_LIST_MODULE_DATA: IPagesListModuleAccess[] = [...RESEARCH_PAGE_LIST_MODULE_DATA, ...UPLOAD_PAGE_LIST_MODULE_DATA];
	private readonly SIDEBAR_CONFIGS	  : ISparkSidebarConfig[] 	= [
		RESEARCH_SIDEBAR_LIST,
		UPLOAD_SIDEBAR_LIST
	];

	@ViewChild('content') private content: ElementRef;
	@ViewChild('favoritePicker') private favoritePicker: TemplateRef<unknown>;

	//** UI Variables */
	userInfo				: IUserInfo | null = null;
	sidebarConfig			: ISparkSidebarConfig = this.SIDEBAR_CONFIGS[0];
	favoriteOptions		: ISidebarFavoriteOption[] = [];
	favoriteSlots			: Array<ISidebarFavoriteOption | null> = [null, null, null, null];
	favoriteSearch			: string = '';
	favoriteProductFilter	: TypeSidebarFavoriteProductFilter = 'all';
	favoritePickerSlot		: number = 0;

	//** Manage Tutorial data */
	moduleInfo 		: IPagesListModuleAccess;

	private readonly subscriptions	: Subscription = new Subscription();
	private readonly initializedExpansionApps: Set<string> = new Set<string>();
	private favIcon					: HTMLLinkElement 	= document.querySelector('#appIcon');
	private favoriteReferences		: Array<ISidebarFavoriteReference | null> = [null, null, null, null];
	private expansionState			: TypeSidebarExpansionState = {};

	constructor(
		private titleService			: Title,
		private router					: Router,
		private statesService			: StatesService,
		private modalAssistantService	: NgModalAssistantService,
		public appearancePreferenceService: AppearancePreferenceService
	) {}


	ngOnInit(): void {
		this.favoriteReferences = this.loadFavoriteReferences();
		this.expansionState = this.loadExpansionState();

		// get user info
		this.subscriptions.add(this.statesService.user.getUserInfo().subscribe((userInfo: IUserInfo) => {
			this.userInfo = userInfo;
		}));

		// Open the section for newly navigated routes without closing sections the user left open.
		this.subscriptions.add(this.router.events.pipe(
			filter((event): event is NavigationEnd => event instanceof NavigationEnd)
		).subscribe((event: NavigationEnd) => {
			this.sidebarConfig = { ...this.handleRouteExpansion(this.sidebarConfig, event.urlAfterRedirects) };
		}));
		//0 - change app title according to selected app
		this.subscriptions.add(this.statesService.appState.getSelectedAppObservable().subscribe((selectedApp: IWebAppTool) => {

			//a. show new title
			this.titleService.setTitle(selectedApp.name);
			this.favIcon.href = selectedApp.logo;

			//b. select app sidebar config
			const selectedSidebarConfig: ISparkSidebarConfig = this.SIDEBAR_CONFIGS.find((sidebarConfig: ISparkSidebarConfig) => sidebarConfig.appInfo.id === selectedApp.tool) || RESEARCH_SIDEBAR_LIST;
			this.sidebarConfig = { ...this.restoreExpansionState(this.handleModuleAccess(selectedSidebarConfig), this.router.url) };
		}));

		//1 - manage subscription Modules
		this.subscriptions.add(this.statesService.access.getAccessRights().subscribe(() => {
			this.sidebarConfig = { ...this.handleModuleAccess(this.sidebarConfig) };
			this.refreshFavoriteOptions();
		}));
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}


	/**------------------------------------------------------
	 * UI Helpers
	 */

	//** Handle avatar */
	onEditAvatarClick(): void {
		const queryParams: { edit_avatar: boolean } = { edit_avatar: true };
		this.router.navigate(['settings', 'account'], { queryParams });
	}

	onMenuCollapseChange(change: ISparkSidebarCollapseChange): void {
		const menu: ISparkSidebarMenu | undefined = this.getExpandableMenus(this.sidebarConfig).find((sidebarMenu: ISparkSidebarMenu) => sidebarMenu.id === change.menuId);
		if (!menu) return;

		menu.isCollapsed = change.isCollapsed;
		this.initializedExpansionApps.add(this.getSidebarAppKey(this.sidebarConfig));
		this.saveExpansionState(this.sidebarConfig);
	}

	//** Favorite shortcuts */
	get favoriteCount(): number {
		return this.favoriteSlots.filter((favorite: ISidebarFavoriteOption | null) => Boolean(favorite)).length;
	}

	get filteredFavoriteOptions(): ISidebarFavoriteOption[] {
		const normalizedSearch: string = this.favoriteSearch.trim().toLowerCase();

		return this.favoriteOptions.filter((option: ISidebarFavoriteOption) => {
			const matchesProduct: boolean = this.favoriteProductFilter === 'all' || option.app === this.favoriteProductFilter;
			const matchesSearch : boolean = !normalizedSearch || `${option.label} ${option.groupLabel} ${option.appLabel}`.toLowerCase().includes(normalizedSearch);
			return matchesProduct && matchesSearch;
		});
	}

	openFavoritePicker(slotIndex: number): void {
		this.favoritePickerSlot = slotIndex;
		this.favoriteSearch = '';
		this.favoriteProductFilter = 'all';
		this.modalAssistantService.openLarge(this.favoritePicker);
	}

	selectFavorite(option: ISidebarFavoriteOption): void {
		if (this.isFavoriteSelected(option)) return;

		this.favoriteReferences[this.favoritePickerSlot] = {
			app		: option.app,
			pageId	: option.pageId
		};
		this.saveFavoriteReferences();
		this.syncFavoriteSlots();
	}

	removeFavorite(event: Event, slotIndex: number): void {
		event.preventDefault();
		event.stopPropagation();
		this.favoriteReferences[slotIndex] = null;
		this.saveFavoriteReferences();
		this.syncFavoriteSlots();
	}

	navigateToFavorite(favorite: ISidebarFavoriteOption): void {
		this.statesService.appState.setSelectedApp(favorite.app);
		this.router.navigate([favorite.routePath], { queryParams: favorite.queryParams });
	}

	isFavoriteSelected(option: ISidebarFavoriteOption): boolean {
		return this.favoriteSlots.some((favorite: ISidebarFavoriteOption | null) => favorite?.key === option.key);
	}

	//** Handle Popup window */
	openModal(moduleName: string): void {

		//0 - find the right page
		const sidebarMenus		: ISparkSidebarMenu[] 	 = this.sidebarConfig.pageLists.flatMap((sidebarList: ISparkSidebarList) => sidebarList.menus);
		const sidebarSubMenus	: ISparkSidebarSubMenu[] = sidebarMenus.flatMap((sidebarMenu: ISparkSidebarMenu) => sidebarMenu.subMenus);
		const subMenu			: ISparkSidebarSubMenu   = sidebarSubMenus.find((sidebarSubMenu: ISparkSidebarSubMenu) => sidebarSubMenu.label === moduleName);
		if (!subMenu) throw new Error(`SidebarComponent => openModal => FATAL ERROR: subMenu was not found for the moduleName of "${moduleName}"`);

		//1 - is module having access
		this.moduleInfo = this.PAGE_LIST_MODULE_DATA.find((data: IPagesListModuleAccess) => data.id === subMenu.id);
		if (!this.moduleInfo) throw new Error(`SidebarComponent => openModal => FATAL ERROR: Clicked link is not having module access. Please check the module access of sidebar.`);

		//2 - open the modal
		this.modalAssistantService.openLarge(this.content);
	}


	/**------------------------------------------------------
	 * Handle Module Access
	 */
	private handleModuleAccess(config: ISparkSidebarConfig): ISparkSidebarConfig {

		//0 - get sidebar menus
		const sidebarMenus: ISparkSidebarMenu[] = config.pageLists.flatMap((sidebarList: ISparkSidebarList) => sidebarList.menus);

		//1 - iterate through sidebar menus
		for (const menu of sidebarMenus) {

			//a. - skip the top header elements
			if (Util.Array.isEmpty(menu.subMenus)) continue;

			//b. limit sidebar menu
			this.limitSidebarMenu(menu);
		}

		//2 - return updated config
		return config;
	}

	private limitSidebarMenu(sidebarMenu: ISparkSidebarMenu) {

		// set the access rights for the users modules
		for (const subMenu of sidebarMenu.subMenus) {

			//a. is this the home page, if so set it
			subMenu.isSelected = subMenu.id === EnumSidebarPage.Home;

			//b. hide the page based on the feature flag
			subMenu.isHidden = this.SIDEBAR_HIDDEN_PAGES.includes(subMenu.id as EnumSidebarPage);

			//c. set the disabled flag
			subMenu.isDisabled = !this.statesService.access.hasAccess(subMenu.routePath);
		}
	}

	private handleRouteExpansion(config: ISparkSidebarConfig, url: string): ISparkSidebarConfig {
		const appKey: string = this.getSidebarAppKey(config);
		if (!this.initializedExpansionApps.has(appKey)) return this.restoreExpansionState(config, url);

		const routePath: string = this.getSelectedRoutePath(config, url);
		const sidebarMenus: ISparkSidebarMenu[] = this.getExpandableMenus(config);

		for (const menu of sidebarMenus) {
			const hasActiveRoute: boolean = menu.subMenus.some((subMenu: ISparkSidebarSubMenu) => subMenu.routePath === routePath);
			if (hasActiveRoute) menu.isCollapsed = false;

			for (const subMenu of menu.subMenus) subMenu.isSelected = subMenu.routePath === routePath;
		}

		this.saveExpansionState(config);
		return config;
	}

	private restoreExpansionState(config: ISparkSidebarConfig, url: string): ISparkSidebarConfig {
		const appKey: string = this.getSidebarAppKey(config);
		const routePath: string = this.getSelectedRoutePath(config, url);
		const sidebarMenus: ISparkSidebarMenu[] = this.getExpandableMenus(config);
		const hasSavedState: boolean = Object.prototype.hasOwnProperty.call(this.expansionState, appKey);
		const expandedMenuIds: Set<string> = new Set<string>(this.expansionState[appKey] || []);

		for (const menu of sidebarMenus) {
			const hasActiveRoute: boolean = menu.subMenus.some((subMenu: ISparkSidebarSubMenu) => subMenu.routePath === routePath);
			menu.isCollapsed = hasActiveRoute ? false : hasSavedState ? !expandedMenuIds.has(menu.id) : true;

			for (const subMenu of menu.subMenus) subMenu.isSelected = subMenu.routePath === routePath;
		}

		this.initializedExpansionApps.add(appKey);
		this.saveExpansionState(config);
		return config;
	}

	private getSelectedRoutePath(config: ISparkSidebarConfig, url: string): string {
		const routePath: string = url.split('?')[0].split('#')[0];
		if (!routePath.startsWith('/research/research/detail/')) return routePath;

		const source: string | null = this.router.parseUrl(url).queryParamMap.get('source');
		const sourceRoutePath: string = source === 'dashboard'
			? '/'
			: `/research/research/${source || 'best-sellers'}`;
		const availableRoutes: Set<string> = new Set(
			this.getExpandableMenus(config).flatMap((menu: ISparkSidebarMenu) => menu.subMenus.map((subMenu: ISparkSidebarSubMenu) => subMenu.routePath))
		);

		return availableRoutes.has(sourceRoutePath) ? sourceRoutePath : '/research/research/best-sellers';
	}

	private getExpandableMenus(config: ISparkSidebarConfig): ISparkSidebarMenu[] {
		return config.pageLists
			.flatMap((sidebarList: ISparkSidebarList) => sidebarList.menus)
			.filter((menu: ISparkSidebarMenu) => !Util.Array.isEmpty(menu.subMenus));
	}

	private getSidebarAppKey(config: ISparkSidebarConfig): string {
		return config.appInfo.id || config.appInfo.label;
	}

	private loadExpansionState(): TypeSidebarExpansionState {
		try {
			const storedValue: string | null = localStorage.getItem(this.EXPANSION_STORAGE_KEY);
			const parsedValue: unknown = storedValue ? JSON.parse(storedValue) : {};
			if (!parsedValue || typeof parsedValue !== 'object' || Array.isArray(parsedValue)) return {};

			const state: TypeSidebarExpansionState = {};
			for (const [appKey, menuIds] of Object.entries(parsedValue)) {
				if (!Array.isArray(menuIds)) continue;
				state[appKey] = menuIds.filter((menuId: unknown): menuId is string => typeof menuId === 'string');
			}
			return state;
		} catch {
			return {};
		}
	}

	private saveExpansionState(config: ISparkSidebarConfig): void {
		const appKey: string = this.getSidebarAppKey(config);
		this.expansionState[appKey] = this.getExpandableMenus(config)
			.filter((menu: ISparkSidebarMenu) => !menu.isCollapsed)
			.map((menu: ISparkSidebarMenu) => menu.id);

		try {
			localStorage.setItem(this.EXPANSION_STORAGE_KEY, JSON.stringify(this.expansionState));
		} catch {
			// Keep the in-memory state when browser storage is unavailable.
		}
	}

	private refreshFavoriteOptions(): void {
		const options: ISidebarFavoriteOption[] = [];

		for (const config of this.SIDEBAR_CONFIGS) {
			const app: EnumWebAppTool = config.appInfo.id as EnumWebAppTool;
			const appLabel: string = app === EnumWebAppTool.Research ? 'Research' : 'Upload';

			for (const list of config.pageLists) {
				for (const menu of list.menus) {
					for (const subMenu of menu.subMenus) {
						if (this.SIDEBAR_HIDDEN_PAGES.includes(subMenu.id as EnumSidebarPage)) continue;
						if (!this.statesService.access.hasAccess(subMenu.routePath)) continue;

						options.push({
							key			: `${app}:${subMenu.id}`,
							pageId		: subMenu.id,
							label		: subMenu.label,
							groupLabel	: menu.label,
							app,
							appLabel,
							routePath	: subMenu.routePath,
							queryParams	: subMenu.queryParams,
							icon		: subMenu.icon || menu.icon
						});
					}
				}
			}
		}

		this.favoriteOptions = options;
		this.syncFavoriteSlots();
	}

	private syncFavoriteSlots(): void {
		this.favoriteSlots = this.favoriteReferences.map((reference: ISidebarFavoriteReference | null) => {
			if (!reference) return null;
			return this.favoriteOptions.find((option: ISidebarFavoriteOption) => option.app === reference.app && option.pageId === reference.pageId) || null;
		});
	}

	private loadFavoriteReferences(): Array<ISidebarFavoriteReference | null> {
		try {
			const storedValue: string | null = localStorage.getItem(this.FAVORITES_STORAGE_KEY);
			const parsedValue: unknown = storedValue ? JSON.parse(storedValue) : [];
			if (!Array.isArray(parsedValue)) return [null, null, null, null];

			return this.FAVORITE_SLOT_INDEXES.map((slotIndex: number) => {
				const reference: unknown = parsedValue[slotIndex];
				return this.isFavoriteReference(reference) ? reference : null;
			});
		} catch {
			return [null, null, null, null];
		}
	}

	private saveFavoriteReferences(): void {
		localStorage.setItem(this.FAVORITES_STORAGE_KEY, JSON.stringify(this.favoriteReferences));
	}

	private isFavoriteReference(value: unknown): value is ISidebarFavoriteReference {
		if (!value || typeof value !== 'object') return false;
		const reference: Partial<ISidebarFavoriteReference> = value as Partial<ISidebarFavoriteReference>;
		return Object.values(EnumWebAppTool).includes(reference.app as EnumWebAppTool) && typeof reference.pageId === 'string';
	}
}


interface ISidebarCommunityLink {
	label   : string;
	icon    : string;
	platform: 'discord' | 'youtube' | 'instagram';
	url     : string;
	badge?  : string;
}

interface ISidebarFavoriteReference {
	app		: EnumWebAppTool;
	pageId	: string;
}

interface ISidebarFavoriteOption extends ISidebarFavoriteReference {
	key			: string;
	label		: string;
	groupLabel	: string;
	appLabel	: string;
	routePath	: string;
	queryParams? : object;
	icon		: ISparkSidebarMenu['icon'];
}

type TypeSidebarFavoriteProductFilter = 'all' | EnumWebAppTool;

type TypeSidebarExpansionState = Record<string, string[]>;
